import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Minus, Plus, ShieldAlert } from "lucide-react";
import { IMG } from "@/lib/images";
import { Chapter, Reveal } from "@/components/Reveal";
import PageHero from "@/components/PageHero";
import { usePageMeta } from "@/lib/site";

const FAQS = [
  {
    q: "Does Black Sky Builders lend money?",
    a: "No. Black Sky Builders is not a lender. For qualified projects, we can help you explore construction financing resources and connect you with appropriate financing resources while your project scope, budget, and construction plan are being developed.",
  },
  {
    q: "Is financing guaranteed?",
    a: "No. We do not guarantee financing, approvals, rates, property values, or lending terms. Financing decisions are made independently by financing providers based on their own criteria.",
  },
  {
    q: "When should we start talking about financing?",
    a: "Early. Understanding potential funding paths while the scope and budget are still being developed leads to better-planned projects — and fewer surprises once construction begins.",
  },
  {
    q: "What should I bring to a project consultation?",
    a: "Your ideas, any existing plans or documents, a rough budget range, and your questions. If you're unsure about cost, financing, permits, plans, or engineering — that's exactly what the consultation is for.",
  },
];

const fmt = (n) => n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export default function Financing() {
  usePageMeta(
    "Project Financing Resources — Utah Construction",
    "For qualified projects, Black Sky Builders helps clients explore construction financing resources and understand potential funding paths while scope and budget are developed."
  );
  const [amount, setAmount] = useState(150000);
  const [term, setTerm] = useState(15);
  const [openIdx, setOpenIdx] = useState(0);

  const r = 0.08 / 12;
  const n = term * 12;
  const monthly = (amount * r) / (1 - Math.pow(1 + r, -n));

  return (
    <div data-testid="financing-page">
      <PageHero
        eyebrow="Project Financing"
        lines={["A Better Project", "Starts With a Better Plan"]}
        copy="Planning a major construction project also means determining how it will be funded. For qualified projects, we can help you explore construction financing resources and better understand potential funding paths while the scope, budget, and construction plan are being developed."
        image={IMG.blueprint}
      />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24 lg:px-16">
        <div className="grid gap-12 lg:grid-cols-2">
          <div>
            <Chapter index="01" eyebrow="How We Help" title="Funding the plan, not just the build" />
            <div className="-mt-4 space-y-4">
              {[
                "Explore available construction financing resources for qualified projects",
                "Understand potential funding paths while scope and budget are developed",
                "Coordinate project timing with your financing process",
                "Connect with appropriate financing resources when needed",
              ].map((item, i) => (
                <Reveal key={item} delay={i * 0.06}>
                  <div className="flex items-start gap-4 border border-line bg-coal p-5">
                    <span className="font-mono text-xs tracking-[0.2em] text-steel">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-sm leading-relaxed text-mist">{item}</span>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.15}>
              <div className="mt-8 flex items-start gap-4 border border-line bg-ink p-5" data-testid="financing-disclaimer">
                <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-silver" />
                <p className="font-mono text-[11px] leading-relaxed tracking-wide text-steel">
                  Black Sky Builders is not a lender and does not provide loans. We do not guarantee financing,
                  approvals, rates, property values, or lending terms. All financing is provided by independent
                  financing resources under their own criteria.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <Link to="/contact?interest=financing" data-testid="financing-ask-button" className="btn-primary mt-8">
                Ask About Project Financing <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>

          <div>
            <Reveal>
              <div className="border border-line bg-coal p-6 sm:p-8" data-testid="financing-calculator">
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-fog">Planning Tool</p>
                <h2 className="mt-3 font-display text-xl font-bold uppercase tracking-tight">
                  Estimate a monthly payment
                </h2>
                <div className="mt-8">
                  <div className="flex items-baseline justify-between">
                    <label htmlFor="fin-amount" className="form-label !mb-0">Project amount</label>
                    <span className="font-display text-2xl font-black tracking-tight" data-testid="financing-calculator-amount-value">{fmt(amount)}</span>
                  </div>
                  <input
                    id="fin-amount"
                    type="range"
                    min={25000}
                    max={1000000}
                    step={5000}
                    value={amount}
                    onChange={(e) => setAmount(Number(e.target.value))}
                    data-testid="financing-calculator-amount-slider"
                    className="mt-4 w-full accent-white"
                  />
                  <div className="mt-2 flex justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-steel">
                    <span>$25k</span><span>$1M</span>
                  </div>
                </div>
                <div className="mt-8">
                  <label htmlFor="fin-term" className="form-label">Term</label>
                  <select
                    id="fin-term"
                    value={term}
                    onChange={(e) => setTerm(Number(e.target.value))}
                    data-testid="financing-calculator-term-select"
                    className="field"
                  >
                    {[10, 15, 20, 30].map((t) => (
                      <option key={t} value={t}>{t} years</option>
                    ))}
                  </select>
                </div>
                <div className="mt-8 border-t border-line pt-6">
                  <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-fog">
                    Illustrative monthly payment @ 8.00% APR
                  </p>
                  <p className="mt-2 font-display text-4xl font-black tracking-tight" data-testid="financing-calculator-output-value">
                    {fmt(monthly)}<span className="text-base font-bold text-fog">/mo</span>
                  </p>
                  <p className="mt-4 font-mono text-[10px] leading-relaxed tracking-wide text-steel">
                    Illustrative estimate only — not a quote, offer, rate, or lending terms. Actual financing depends
                    on independent financing providers.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-t border-line bg-coal">
        <div className="mx-auto max-w-4xl px-4 py-16 sm:px-8 sm:py-24" data-testid="financing-faq">
          <Chapter index="02" eyebrow="Common Questions" title="Financing FAQ" />
          <div className="-mt-4 divide-y divide-line border border-line bg-ink">
            {FAQS.map((faq, i) => (
              <div key={faq.q}>
                <button
                  onClick={() => setOpenIdx(openIdx === i ? -1 : i)}
                  aria-expanded={openIdx === i}
                  data-testid={`financing-faq-${i}-button`}
                  className="flex w-full items-center justify-between gap-4 p-5 text-left"
                >
                  <span className="font-display text-sm font-bold uppercase tracking-wide sm:text-base">{faq.q}</span>
                  {openIdx === i ? <Minus className="h-4 w-4 shrink-0 text-silver" /> : <Plus className="h-4 w-4 shrink-0 text-fog" />}
                </button>
                <AnimatePresence initial={false}>
                  {openIdx === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <p className="px-5 pb-6 text-sm leading-relaxed text-fog">{faq.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
